import { Browser, MobileOS } from "../models/IDevice";

export interface DeviceInfo {
  // Browser Information
  browser: {
    name: Browser;
    version: string;
    engine: string;
  };

  // Operating System
  os: {
    name: MobileOS;
    version: string;
    platform: string;
  };

  // Device Information
  device: {
    type: 'desktop' | 'mobile' | 'tablet' | 'tv' | 'unknown';
    vendor: string;
    model: string;
    isMobile: boolean;
    isTablet: boolean;
    isDesktop: boolean;
    isTouchDevice: boolean;
  };

  // Additional Capabilities
  capabilities: {
    isWebGLSupported: boolean;
    isWebRTCSupported: boolean;
    isServiceWorkerSupported: boolean;
    isTouchSupported: boolean;
    cookiesEnabled: boolean;
    javaEnabled: boolean;
    screenResolution: string;
    colorDepth: number;
    timezone: string;
  };

  // Raw user agent
  userAgent: string;
}

export function parseUserAgent(userAgent: string = navigator.userAgent): DeviceInfo {
  const ua = userAgent.toLowerCase();

  // Browser Detection
  const getBrowserInfo = (): DeviceInfo['browser'] => {
    let name = Browser.Unknown;
    let version = 'unknown';
    let engine = 'unknown';

    // Edge (Chromium-based)
    if (ua.includes('edg/') || ua.includes('edge/')) {
      name = Browser.Edge;
      const match = ua.match(/edg?\/([0-9.]+)/);
      version = match ? match[1] : 'unknown';
      engine = 'blink';
    }
    // Chrome
    else if (ua.includes('chrome/') && !ua.includes('edg')) {
      name = Browser.Chrome;
      const match = ua.match(/chrome\/([0-9.]+)/);
      version = match ? match[1] : 'unknown';
      engine = 'blink';
    }
    // Firefox
    else if (ua.includes('firefox/')) {
      name = Browser.Firefox;
      const match = ua.match(/firefox\/([0-9.]+)/);
      version = match ? match[1] : 'unknown';
      engine = 'gecko';
    }
    // Safari
    else if (ua.includes('safari/') && !ua.includes('chrome')) {
      name = Browser.Safari;
      const match = ua.match(/version\/([0-9.]+)/);
      version = match ? match[1] : 'unknown';
      engine = 'webkit';
    }
    // Opera
    else if (ua.includes('opera') || ua.includes('opr/')) {
      name = Browser.Opera;
      const match = ua.match(/(?:opera|opr)\/([0-9.]+)/);
      version = match ? match[1] : 'unknown';
      engine = 'blink';
    }
    // Internet Explorer
    else if (ua.includes('msie') || ua.includes('trident/')) {
      name = Browser.InternetExplorer;
      const match = ua.match(/(?:msie |rv:)([0-9.]+)/);
      version = match ? match[1] : 'unknown';
      engine = 'trident';
    }
    // Samsung Browser
    else if (ua.includes('samsungbrowser/')) {
      name = Browser.Samsung;
      const match = ua.match(/samsungbrowser\/([0-9.]+)/);
      version = match ? match[1] : 'unknown';
      engine = 'blink';
    }
    // UC Browser
    else if (ua.includes('ucbrowser/')) {
      name = Browser.Unknown;
      const match = ua.match(/ucbrowser\/([0-9.]+)/);
      version = match ? match[1] : 'unknown';
      engine = 'webkit';
    } else if (/XiaoMi/i.test(ua) || /MiuiBrowser/i.test(ua)) {
      name = Browser.Mi;
      const match = ua.match(/miuibrowser\/([0-9.]+)/);
      version = match ? match[1] : 'unknown';
      engine = 'webkit';
    }

    return { name, version, engine };
  };

  // Operating System Detection
  const getOSInfo = (): DeviceInfo['os'] => {
    let name = MobileOS.Unknown;
    let version = 'unknown';
    let platform = 'unknown';

    // Windows
    if (ua.includes('windows nt')) {
      name = MobileOS.Windows;
      platform = 'pc';
      const match = ua.match(/windows nt ([0-9.]+)/);
      if (match) {
        const winVersion = match[1];
        switch (winVersion) {
          case '10.0':
            version = '10';
            break;
          case '6.3':
            version = '8.1';
            break;
          case '6.2':
            version = '8';
            break;
          case '6.1':
            version = '7';
            break;
          case '6.0':
            version = 'vista';
            break;
          default:
            version = winVersion;
        }
      }
    }
    // macOS
    else if (ua.includes('mac os x')) {
      name = MobileOS.MacOS;
      platform = 'mac';
      const match = ua.match(/mac os x ([0-9_]+)/);
      version = match ? match[1].replace(/_/g, '.') : 'unknown';
    }
    // iOS
    else if (ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod')) {
      name = MobileOS.iOS;
      platform = 'mobile';
      const match = ua.match(/os ([0-9_]+)/);
      version = match ? match[1].replace(/_/g, '.') : 'unknown';
    }
    // Android
    else if (ua.includes('android')) {
      name = MobileOS.Android;
      platform = 'mobile';
      const match = ua.match(/android ([0-9.]+)/);
      version = match ? match[1] : 'unknown';
    }
    // Linux
    else if (ua.includes('linux')) {
      name = MobileOS.Linux;
      platform = 'pc';
      // Try to detect specific distributions
      if (ua.includes('ubuntu')) version = 'ubuntu';
      else if (ua.includes('fedora')) version = 'fedora';
      else if (ua.includes('debian')) version = 'debian';
    }
    // Chrome OS
    else if (ua.includes('cros')) {
      name = MobileOS.ChromeOS;
      platform = 'pc';
      const match = ua.match(/cros ([a-z0-9_]+) ([0-9.]+)/);
      version = match ? match[2] : 'unknown';
    }

    return { name, version, platform };
  };

  // Device Detection
  const getDeviceInfo = (): DeviceInfo['device'] => {
    let type: DeviceInfo['device']['type'] = 'unknown';
    let vendor = 'unknown';
    let model = 'unknown';

    // Mobile/Tablet Detection
    const isMobile = /android|iphone|ipod|blackberry|iemobile|opera mini/i.test(ua);
    const isTablet = /ipad|android(?!.*mobile)|tablet|kindle|silk/i.test(ua);
    const isDesktop = !isMobile && !isTablet;

    // Device Type
    if (isTablet) {
      type = 'tablet';
    } else if (isMobile) {
      type = 'mobile';
    } else if (ua.includes('smart-tv') || ua.includes('smarttv')) {
      type = 'tv';
    } else {
      type = 'desktop';
    }

    // Vendor & Model Detection
    if (ua.includes('iphone')) {
      vendor = 'apple';
      model = 'iphone';
      // Try to detect iPhone model
      if (ua.includes('iphone os 15')) model = 'iphone 13';
      else if (ua.includes('iphone os 14')) model = 'iphone 12';
    } else if (ua.includes('ipad')) {
      vendor = 'apple';
      model = 'ipad';
    } else if (ua.includes('samsung')) {
      vendor = 'samsung';
      const match = ua.match(/samsung[- ]?([a-z0-9\-]+)/);
      model = match ? match[1] : 'samsung';
    } else if (ua.includes('pixel')) {
      vendor = 'google';
      model = 'pixel';
    } else if (ua.includes('huawei')) {
      vendor = 'huawei';
      model = 'huawei';
    } else if (ua.includes('xiaomi')) {
      vendor = 'xiaomi';
      model = 'xiaomi';
    } else if (ua.includes('oneplus')) {
      vendor = 'oneplus';
      model = 'oneplus';
    }

    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    return {
      type,
      vendor,
      model,
      isMobile,
      isTablet,
      isDesktop,
      isTouchDevice,
    };
  };

  // Capability Detection
  const getCapabilities = (): DeviceInfo['capabilities'] => {
    // WebGL Support
    const isWebGLSupported = (() => {
      try {
        const canvas = document.createElement('canvas');
        return !!(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
      } catch (e) {
        return false;
      }
    })();

    // WebRTC Support
    const isWebRTCSupported = !!(window.RTCPeerConnection || (window as any).webkitRTCPeerConnection || (window as any).mozRTCPeerConnection);

    // Service Worker Support
    const isServiceWorkerSupported = 'serviceWorker' in navigator;

    // Touch Support
    const isTouchSupported = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // Screen Information
    const screenResolution = `${screen.width}x${screen.height}`;
    const colorDepth = screen.colorDepth || 0;

    // Timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return {
      isWebGLSupported,
      isWebRTCSupported,
      isServiceWorkerSupported,
      isTouchSupported,
      cookiesEnabled: navigator.cookieEnabled,
      javaEnabled: (navigator as any).javaEnabled ? (navigator as any).javaEnabled() : false,
      screenResolution,
      colorDepth,
      timezone,
    };
  };

  return {
    browser: getBrowserInfo(),
    os: getOSInfo(),
    device: getDeviceInfo(),
    capabilities: getCapabilities(),
    userAgent: userAgent,
  };
}

